import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useMemo } from 'react';

/**
 * Remembers a grid's column layout (order, hidden, pinned) per user and org, across all tabs.
 * Grids with the same set of columns share one layout.
 */
export const useGridLayout = () => {
  // Take principal and org out of context
  const { principal, org } = usePrincipal();

  // Memoize so we only rebuild for different user or org
  return useMemo(() => {
    // The sorted column ids identify the grid, so no key needs to be passed in
    const storageKey = (api) => {
      const colIds = (api.getColumns() ?? []).map((col) => col.getColId()).sort();
      return `gridLayout:${principal.username}:${org}:${colIds.join('|')}`;
    };

    // Return both functions for Grid
    return {
      // Applies the saved layout, if there is one
      restore: (api) => {
        const layout = JSON.parse(localStorage.getItem(storageKey(api)));
        if (layout) {
          api.applyColumnState({ state: layout, applyOrder: true });
        }
      },

      // Saves the current layout; array order is the column order
      save: (api) => {
        const layout = api.getColumnState().map(({ colId, hide, pinned }) => ({ colId, hide, pinned }));
        localStorage.setItem(storageKey(api), JSON.stringify(layout));
      },
    };
  }, [principal.username, org]);
};
